import React, { useEffect, useState } from 'react';
import useDeckStates from '../../hooks/useDeckStates';
import AppsTab from '../Apps/AppsTab';
import InputAutomationTrainer from '../AreaOverview/InputAutomationTrainer';
import WirelessDebuggingTrainer from '../AreaOverview/WirelessDebuggingTrainer';
import AudioRoutingTab from './AudioRoutingTab';
import PowerControlTab from './PowerControlTab';
import PowerLogTab from './PowerLogTab';
import SiteControlTab from './SiteControlTab';
import DeviceConfigTab from './DeviceConfigTab';
import useDeviceConfigSnapshot from './useDeviceConfigSnapshot';
import VirtualMatrixTab from './VirtualMatrixTab';
import './SiteControl.css';

const SITE_CONTROL_TABS = [
  'Pairing',
  'Virtual Matrix',
  'Power Control',
  'Power Log',
  'Input Training',
  'Audio Routing',
  'Apps',
  'Groups',
];

function SiteControl({
  device,
  site,
  area,
  siteAreas,
  devices,
  topology,
  onTopologyChange,
  onDeviceSelect,
  onOpenDeviceRemote,
  onOpenAndroidPowerSettings,
  powerDiagnosticsRequest,
  onManageDeviceApps,
  onDeviceRenamed,
  onEquipmentChange,
}) {
  const [selectedTab, setSelectedTab] = useState(powerDiagnosticsRequest ? 'Power Control' : device?.id ? 'Pairing' : 'Power Control');
  useEffect(() => {
    if (powerDiagnosticsRequest) setSelectedTab('Power Control');
  }, [powerDiagnosticsRequest]);
  const siteId = site?.id || area?.site_id || device?.siteId || device?.site_id || null;
  const deviceConfig = useDeviceConfigSnapshot(siteId);
  const showDeviceConfig = deviceConfig?.available === true || deviceConfig?.hasSavedCaptures === true || deviceConfig?.hasSavedApkUpdates === true;
  const tabs = device?.id ? [...SITE_CONTROL_TABS] : ['Power Control', 'Power Log'];
  if (showDeviceConfig) tabs.splice(device?.id ? 1 : 0, 0, 'Device Config');
  useEffect(() => {
    if (selectedTab === 'Device Config') {
      if (!showDeviceConfig) setSelectedTab(device?.id ? 'Pairing' : 'Power Control');
    } else if (!device?.id && !['Power Control', 'Power Log'].includes(selectedTab)) setSelectedTab('Power Control');
  }, [device?.id, selectedTab, showDeviceConfig]);
  const [trainingTarget, setTrainingTarget] = useState(null);
  const [powerSetupPhase, setPowerSetupPhase] = useState(null);
  const selectedScreen = (topology?.screens || []).find((screen) => Number(screen.televisionDeviceId) === Number(device?.id));
  const preferredSource = selectedScreen?.players?.find((player) => Number(player.deviceId || player.id) === Number(selectedScreen.preferredPlayerDeviceId));
  const preferredPort = Number(String(preferredSource?.expectedInput || '').match(/HDMI\s*([1-8])/i)?.[1]) || null;
  const target = trainingTarget?.deviceId === device?.id ? trainingTarget : null;
  const openInputTraining = (screenId, port) => {
    setTrainingTarget({ screenId, port, deviceId: device?.id });
    setSelectedTab('Input Training');
  };
  const { externalApp } = useDeckStates(device);

  return (
    <section className="site-control">
      <div className="site-control__tabs" role="tablist" aria-label="Site Control">
        {tabs.map((tabName) => (
          <button
            key={tabName}
            type="button"
            role="tab"
            aria-selected={selectedTab === tabName}
            className={`tab-button ${selectedTab === tabName ? 'active' : ''}`}
            onClick={() => { setTrainingTarget(null); setPowerSetupPhase(null); setSelectedTab(tabName); }}
          >
            {tabName}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={selectedTab}>
        {siteId && <div hidden={selectedTab !== 'Device Config'}>
          <DeviceConfigTab key={siteId} siteId={siteId} snapshot={deviceConfig} device={device}
            active={selectedTab === 'Device Config'} onDeviceRenamed={onDeviceRenamed} />
        </div>}
        {selectedTab === 'Pairing' && <SiteControlTab device={device} site={site} onEquipmentChange={onEquipmentChange} onOpenDeviceRemote={onOpenDeviceRemote} />}
        {selectedTab === 'Power Control' && (
          <PowerControlTab
            key={site?.id || area?.site_id || device?.siteId || device?.site_id || 'no-site'}
            device={device}
            area={area}
            siteId={site?.id || area?.site_id || device?.siteId || device?.site_id}
            devices={devices}
            topology={topology}
            onTopologyChange={onTopologyChange}
            onOpenInputTraining={openInputTraining}
            onOpenPowerLog={() => setSelectedTab('Power Log')}
            initialSetupPhase={powerSetupPhase}
            diagnosticsRequest={powerDiagnosticsRequest}
          />
        )}
        {selectedTab === 'Power Log' && <PowerLogTab siteId={site?.id || area?.site_id || device?.siteId || device?.site_id}
          device={device} area={area} topology={topology} onOpenPowerControl={(phase) => {
            setPowerSetupPhase(phase);
            setSelectedTab('Power Control');
          }} />}
        {selectedTab === 'Input Training' && (
          <>
            <InputAutomationTrainer
              key={`${device?.id}-${target?.screenId || selectedScreen?.id}-${target?.port || ''}`}
              initialScreenId={target?.screenId || selectedScreen?.id || ''}
              initialPort={target?.port || preferredPort}
              areaId={area?.id}
              siteAreas={siteAreas}
              topology={topology}
              devices={devices}
              onTopologyChange={onTopologyChange}
              onConfigureScreen={() => setSelectedTab('Virtual Matrix')}
            />
            <WirelessDebuggingTrainer siteId={siteId} device={device} />
          </>
        )}
        {selectedTab === 'Virtual Matrix' && (
          <VirtualMatrixTab
            area={area}
            device={device}
            onDeviceSelect={onDeviceSelect}
            onOpenDeviceRemote={onOpenDeviceRemote}
            onOpenAndroidPowerSettings={onOpenAndroidPowerSettings}
            onTopologyChange={onTopologyChange}
          />
        )}
        {selectedTab === 'Audio Routing' && (
          <AudioRoutingTab
            device={device}
            area={area}
            topology={topology}
            onTopologyChange={onTopologyChange}
          />
        )}
        {selectedTab === 'Apps' && (
          <AppsTab
            siteId={siteId}
            device={device}
            externalApp={externalApp}
            onManageDeviceApps={onManageDeviceApps}
          />
        )}
      </div>
    </section>
  );
}

export default SiteControl;
